import { useState } from "react";
import { Button, ErrorText, Field, Panel, Spinner } from "../../components/ui";
import { fmtAmount, fmtDate } from "../../lib/format";
import { useCreateSgi, useSgiDashboard, useSgis, useUpdateSgi } from "./hooks";
import type { CreateSgiInput } from "./api";

const EMPTY: CreateSgiInput = { name: "", code: "", defaultCommissionRate: "0.012", vatRate: "0.18" };

export function SgisPage() {
  const { data, isLoading, error } = useSgis();
  const dashboard = useSgiDashboard();
  const create = useCreateSgi();
  const update = useUpdateSgi();
  const [form, setForm] = useState<CreateSgiInput | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    create.mutate({ ...form, code: form.code || null }, { onSuccess: () => setForm(null) });
  }

  return (
    <Panel
      title="SGI"
      actions={
        <Button onClick={() => setForm(form ? null : { ...EMPTY })}>
          {form ? "Annuler" : "Nouvelle SGI"}
        </Button>
      }
    >
      {form && (
        <form className="inline-form" onSubmit={submit}>
          <Field
            label="Nom"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <Field
            label="Code"
            value={form.code ?? ""}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
          <Field
            label="Commission (0–1)"
            value={form.defaultCommissionRate ?? ""}
            onChange={(e) => setForm({ ...form, defaultCommissionRate: e.target.value })}
          />
          <Field
            label="TVA (0–1)"
            value={form.vatRate ?? ""}
            onChange={(e) => setForm({ ...form, vatRate: e.target.value })}
          />
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "…" : "Créer"}
          </Button>
          <ErrorText error={create.error} />
        </form>
      )}

      <ErrorText error={error} />
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Code</th>
                <th>Commission</th>
                <th>TVA</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.code ?? "—"}</td>
                  <td>{s.defaultCommissionRate ?? "—"}</td>
                  <td>{s.vatRate ?? "—"}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={s.active}
                      disabled={update.isPending}
                      onChange={(e) => update.mutate({ id: s.id, patch: { active: e.target.checked } })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="panel-subhead">Volume par SGI (transactions confirmées)</h3>
      <ErrorText error={dashboard.error} />
      {dashboard.isLoading ? (
        <Spinner />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>SGI</th>
                <th>Clients</th>
                <th>Transactions</th>
                <th>Volume achats</th>
                <th>Commissions perçues</th>
                <th>Dernière opération</th>
              </tr>
            </thead>
            <tbody>
              {dashboard.data?.map((s) => (
                <tr key={s.sgi_id}>
                  <td>
                    {s.name}
                    {s.code ? ` (${s.code})` : ""}
                  </td>
                  <td>{s.client_count}</td>
                  <td>{s.transaction_count}</td>
                  <td>{fmtAmount(s.buy_notional)}</td>
                  <td>{fmtAmount(s.commissions_collected)}</td>
                  <td>{fmtDate(s.last_trade_date)}</td>
                </tr>
              ))}
              {dashboard.data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    Aucune donnée.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
